import { $, $$ } from '../lib/dom.js';
import { clamp, passP, overP, centerPeak } from '../lib/motion.js';

const updaters = [];

function addUpdater(el, apply) {
  if (el) updaters.push({ el, apply });
}
function addAll(selector, apply) {
  document.querySelectorAll(selector).forEach(el => updaters.push({ el, apply }));
}

function wrapWords(root) {
  if (!root || root.dataset.wrapped) return;
  root.dataset.wrapped = '1';
  const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let n;
  while ((n = walk.nextNode())) textNodes.push(n);
  textNodes.forEach(t => {
    const parent = t.parentNode;
    const txt = t.textContent;
    if (!txt.trim()) return;
    const frag = document.createDocumentFragment();
    txt.split(/(\s+)/).forEach(part => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        frag.appendChild(document.createTextNode(part));
      } else {
        const w = document.createElement('span');
        w.className = 'word';
        const style = (parent.getAttribute && parent.getAttribute('style')) || '';
        if (/--blaze/.test(style) || /#ff5b1f/i.test(style)) w.dataset.color = 'blaze';
        else if (/--lime/.test(style) || /#c9ff3a/i.test(style)) w.dataset.color = 'lime';
        w.textContent = part;
        frag.appendChild(w);
      }
    });
    parent.replaceChild(frag, t);
  });
}

export function initScrollFx() {
  const bar = document.createElement('div');
  bar.id = 'scroll-progress';
  document.body.appendChild(bar);

  function checkScrolled() {
    document.body.classList.toggle('has-scrolled', window.scrollY > 80);
  }
  checkScrolled();
  window.addEventListener('scroll', checkScrolled, { passive: true });

  // hero + section parallax wiring
  addAll('.hero .meta-row', function (r, vh) {
    this.el.style.setProperty('--pp', overP(r, vh, 1.0).toFixed(3));
  });
  addAll('.hero h1', function (r, vh) {
    this.el.style.setProperty('--pp', overP(r, vh, 1.2).toFixed(3));
  });
  addAll('.hero .below', function (r, vh) {
    this.el.style.setProperty('--pp', overP(r, vh, 1.4).toFixed(3));
  });
  addAll('.hero-stats', function (r, vh) {
    const p = clamp(1 - r.top / (vh * 0.9));
    this.el.style.setProperty('--pp', p.toFixed(3));
    const cells = this.el.querySelectorAll('.cell');
    const n = cells.length;
    cells.forEach((c, i) => {
      const delay = (i / Math.max(1, n - 1)) * 0.45;
      c.style.setProperty('--pp', clamp((p - delay) * 1.9).toFixed(3));
    });
  });

  addAll('.section .eyebrow, .recruit .eyebrow', function (r, vh) {
    this.el.style.setProperty('--vp', clamp(1 - r.top / (vh * 0.85)).toFixed(3));
  });

  // word-by-word karaoke
  const sq = $('.scroll-quote');
  if (sq) {
    wrapWords(sq);
    const words = Array.from(sq.querySelectorAll('.word'));
    const section = sq.closest('section');
    addUpdater(section || sq, function (r, vh) {
      const p = clamp((vh - r.top - r.height * 0.25) / (r.height * 0.8 + vh * 0.25));
      const upTo = Math.floor(p * words.length * 1.18 + 0.5);
      for (let i = 0; i < words.length; i++) {
        const w = words[i];
        const lit = i < upTo;
        if (lit) {
          if (w.dataset.color === 'blaze') {
            if (!w.classList.contains('lit-blaze')) {
              w.classList.remove('lit', 'lit-lime');
              w.classList.add('lit-blaze');
            }
          } else if (w.dataset.color === 'lime') {
            if (!w.classList.contains('lit-lime')) {
              w.classList.remove('lit', 'lit-blaze');
              w.classList.add('lit-lime');
            }
          } else if (!w.classList.contains('lit')) {
            w.classList.remove('lit-blaze', 'lit-lime');
            w.classList.add('lit');
          }
        } else if (w.className !== 'word') {
          w.className = 'word';
        }
      }
    });
  }

  // sponsors lateral pan
  addAll('.sponsors', function (r, vh) {
    const row = this.el.querySelector('.row');
    if (row) row.style.setProperty('--pp', passP(r, vh).toFixed(3));
  });

  // recruit headline drift
  addAll('.recruit h2', function (r, vh) {
    const p = clamp((vh - r.top) / (vh + r.height) - 0.3);
    this.el.style.setProperty('--pp', p.toFixed(3));
  });

  // player silhouette + photo-num parallax
  $$('.player').forEach(player => {
    if (player.dataset.fxBound) return;
    player.dataset.fxBound = '1';
    const sil = player.querySelector('.silhouette');
    const num = player.querySelector('.photo-num');
    updaters.push({
      el: player,
      apply(r, vh) {
        const center = r.top + r.height / 2;
        const p = clamp((center - vh / 2) / vh, -1, 1);
        if (sil) sil.style.setProperty('--pp', (-p).toFixed(3));
        if (num) num.style.setProperty('--pp', (-p).toFixed(3));
      }
    });
  });

  // footer signoff stretch
  const signoff = $('footer .signoff');
  if (signoff) {
    addUpdater(signoff, function (r, vh) {
      signoff.style.setProperty('--vp', centerPeak(r, vh).toFixed(3));
    });
  }
  addAll('footer .signoff-meta, footer .colo', function (r, vh) {
    this.el.style.setProperty('--vp', clamp((vh - r.top) / (vh * 0.6)).toFixed(3));
  });

  // one-shot reveals via IO
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

  function watch(sel) {
    document.querySelectorAll(sel).forEach(el => {
      if (!el.dataset.fxWatched) {
        el.dataset.fxWatched = '1';
        io.observe(el);
      }
    });
  }

  $$('.player').forEach(c => {
    if (!c.classList.contains('rv-card')) {
      c.classList.add('rv-card');
      io.observe(c);
    }
  });
  watch('.rules .rule');
  watch('.schedule .match');
  watch('.schedule .empty-slot');
  watch('.faq details');
  watch('.big-quote');
  watch('.lab');
  watch('.share-card');
  watch('.recruit .form');

  // ticker velocity + main tick loop
  const tickerTrack = $('.ticker-track');
  let lastSY = window.scrollY;
  let smoothV = 0;

  function tick() {
    const sy = window.scrollY;
    const vh = window.innerHeight;
    const docH = (document.documentElement.scrollHeight - vh) || 1;
    bar.style.transform = 'scaleX(' + (sy / docH).toFixed(4) + ')';

    const v = Math.abs(sy - lastSY);
    lastSY = sy;
    smoothV = smoothV * 0.85 + v * 0.15;
    if (tickerTrack) {
      const dur = Math.max(12, 38 - smoothV * 1.6);
      tickerTrack.style.setProperty('--ticker-dur', dur.toFixed(1) + 's');
    }

    for (let i = 0; i < updaters.length; i++) {
      const u = updaters[i];
      const r = u.el.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) continue;
      u.apply.call(u, r, vh);
    }
    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);

  // expose watch() so form-submit can register the confirmation panel
  return { watch: el => {
    if (el && !el.dataset.fxWatched) {
      el.dataset.fxWatched = '1';
      io.observe(el);
    }
  }};
}
